import * as Cmp from "@impero-com/common/utils/comparison";
import { formatDate } from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    DisabledLink,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
    TST_BLESSED_ID_COLUMN_SELECTION,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getPaginationRowModel,
    Column,
    RowSelectionState,
    OnChangeFn,
} from "@tanstack/react-table";
import { Checkbox, Select } from "antd";
import moment from "moment-timezone";
import { BaseOptionType } from "rc-cascader/lib/Cascader";

import type { TestingActivityCurrentStep } from "api/generated/model";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { DateTimeRangeFilterValue } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeTypes";
import { TagsCascader } from "components/table/tagsFilter/tagsCascader";
import { TagAndCategory } from "components/tag/tagAndCategory/tagAndCategory";
import { formatPeriodStrings } from "org/control/view/utils/formatPeriodStrings";
import { buildLegacyControlActivityUrl } from "org/controlTesting/urlUtils";
import { formatTagRecordAsString } from "org/tags/tagsTSTUtils";

import { ControlSample } from "../../types";
import { useControlActivityStatusLabelMap } from "../SelectedSamplesTable/consts";

import * as theme from "./AddSamplesTable.css";

type RangeValue = [moment.Moment, moment.Moment];

const TITLE_COLUMN_ID = "title";
const DUE_DATE_COLUMN_ID = "dueDate";
const PERIOD_COLUMN_ID = "period";
const TAGS_COLUMN_ID = "tags";
const STATUS_COLUMN_ID = "status";

type SampleColumnId =
    | typeof TITLE_COLUMN_ID
    | typeof DUE_DATE_COLUMN_ID
    | typeof PERIOD_COLUMN_ID
    | typeof TAGS_COLUMN_ID
    | typeof STATUS_COLUMN_ID;

interface UseAddSamplesTableProps {
    data: ControlSample[];
    selectedRows: RowSelectionState;
    onSelectedRowsChange: OnChangeFn<RowSelectionState>;
}

export function useAddSamplesTable({
    data,
    selectedRows,
    onSelectedRowsChange,
}: UseAddSamplesTableProps) {
    const { t } = useTranslation();
    const controlActivityStatusLabelMap = useControlActivityStatusLabelMap();
    const columns = buildColumns(t, controlActivityStatusLabelMap);

    const table = useReactTable({
        data,
        columns,
        enableRowSelection: true,
        onRowSelectionChange: onSelectedRowsChange,
        state: {
            rowSelection: selectedRows,
        },
        initialState: {
            sorting: [
                {
                    id: TITLE_COLUMN_ID,
                    desc: false,
                },
            ],
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
            },
            columnPinning: {
                left: [TST_BLESSED_ID_COLUMN_SELECTION],
            },
        },
        defaultColumn: {
            size: TST_COLUMN_SIZE.L,
        },
        enableSortingRemoval: false,
        enablePinning: true,
        enableColumnPinning: true,
        getRowId: (row) => row.activityId,
        getCoreRowModel: getCoreRowModel<ControlSample>(),
        getSortedRowModel: getSortedRowModel<ControlSample>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlSample>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlSample>(),
        getFilteredRowModel: getFilteredRowModel<ControlSample>(),
        getPaginationRowModel: getPaginationRowModel<ControlSample>(),
    });

    const filterComponent = (column: Column<ControlSample>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
    };
}

const columnHelper = createColumnHelper<ControlSample>();

const buildColumns = (
    t: TFunction,
    controlActivityStatusLabelMap: Record<TestingActivityCurrentStep, string>
) => [
    columnHelper.display({
        id: TST_BLESSED_ID_COLUMN_SELECTION,
        cell: (info) => {
            const onClick = () => {
                info.row.toggleSelected();
            };
            return (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={onClick}
                />
            );
        },
        header: (info) => {
            return (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            );
        },
        size: TST_COLUMN_SIZE.CHECKBOX,
        meta: { fixedWidth: true },
    }),
    columnHelper.accessor((row) => row.title, {
        id: TITLE_COLUMN_ID,
        enableColumnFilter: false,
        cell: (info) => {
            const row = info.row.original;
            return row.hasViewRight ? (
                <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={buildLegacyControlActivityUrl(row.activityId)}
                >
                    {info.getValue()}
                </a>
            ) : (
                <DisabledLink reason={t("InsufficientAccess")}>
                    {info.getValue()}
                </DisabledLink>
            );
        },
        enableSorting: true,
        enableHiding: false,
        sortingFn: "alphanumericCaseSensitive",
        header: () => t("ControlTestingActivityView_NameColumn"),
        size: TST_COLUMN_SIZE.XL,
    }),
    columnHelper.accessor((row) => row.dueDate, {
        id: DUE_DATE_COLUMN_ID,
        cell: (info) => {
            const value = info.getValue();
            return value ? formatDate(value) : "";
        },
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: Cmp.map((row) => row.original.dueDate, Cmp.primitive),
        filterFn: (row, _id, filterValue: RangeValue) =>
            dateTimeFilterFn(
                filterValue,
                row.original.dueDate?.toISOString() || null
            ),
        header: () => t("ControlTestingActivityView_DueDateColumn"),
        size: TST_COLUMN_SIZE.L,
    }),
    columnHelper.accessor(
        (row) =>
            row.coveredPeriod
                ? formatPeriodStrings(t, row.coveredPeriod).label
                : "",
        {
            id: PERIOD_COLUMN_ID,
            cell: (info) => {
                return info.getValue();
            },
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: (row, id, filterValue: string[] | undefined) => {
                if (!filterValue || filterValue.length === 0) {
                    return true;
                }
                return filterValue.includes(row.getValue(id));
            },
            header: () => t("ControlTestingActivityView_PeriodColumn"),
            size: TST_COLUMN_SIZE.L,
        }
    ),
    columnHelper.accessor((row) => formatTagRecordAsString(row.tags), {
        id: TAGS_COLUMN_ID,
        cell: (info) => {
            const tagComponents = Object.entries(
                info.row.original.tags
            ).flatMap(([tagCategoryName, tagNames]) => {
                return tagNames.map((tagName) => (
                    <TagAndCategory
                        key={`${tagCategoryName}-${tagName}`}
                        categoryName={tagCategoryName}
                        tagName={tagName}
                    />
                ));
            });
            return (
                <div className={theme.tagsCellContainer}>{tagComponents}</div>
            );
        },
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: (row, _id, filterValue: BaseOptionType[][]) => {
            // This filterFn handles tag and tagCategory strings (not IDs), as a value of the antd.Cascader component
            if (!filterValue || filterValue.length === 0) {
                return true;
            }

            const rowTags = row.original.tags;

            return filterValue.some((filterPath) => {
                if (filterPath.length === 0 || !filterPath[0].value) {
                    return false;
                }

                const categoryName = filterPath[0].value as string;

                // If only category is selected (no specific tags), check if any tag in that category exists
                if (filterPath.length === 1) {
                    const hasAnyTagInCategory =
                        rowTags[categoryName] &&
                        rowTags[categoryName].length > 0;
                    return hasAnyTagInCategory;
                }

                // If specific tags are selected, check if any of them exist in the row
                return filterPath.slice(1).some((selectedTag) => {
                    if (selectedTag.value) {
                        const tagName = selectedTag.value as string;
                        return (
                            rowTags[categoryName] &&
                            rowTags[categoryName].includes(tagName)
                        );
                    }
                    return false;
                });
            });
        },
        header: () => t("ControlTestingActivityView_TagsColumn"),
        size: TST_COLUMN_SIZE.L,
    }),
    columnHelper.accessor(
        (row) => controlActivityStatusLabelMap[row.controlStatus],
        {
            id: STATUS_COLUMN_ID,
            cell: (info) => info.getValue(),
            enableSorting: true,
            enableColumnFilter: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: (row, id, filterValue: string[] | undefined) => {
                if (!filterValue || filterValue.length === 0) {
                    return true;
                }
                return filterValue.includes(row.getValue(id));
            },
            header: () => t("ControlTestingActivityView_StatusColumn"),
            size: TST_COLUMN_SIZE.L,
        }
    ),
];

const dateTimeFilterFn = (filterValue: RangeValue, dateStr: string | null) => {
    if (!filterValue || !dateStr) {
        return true;
    }
    const valueTime = new Date(dateStr).getTime();
    const startTime = Number(filterValue[0] ? filterValue[0] : 0);
    const endTime = Number(filterValue[1] ? filterValue[1] : Infinity);

    return valueTime >= startTime && valueTime <= endTime;
};

function getFilterComponent(t: TFunction, column: Column<ControlSample>) {
    const columnId = column.id as SampleColumnId;
    switch (columnId) {
        case "dueDate": {
            return (
                <FilterBarDateTimeRange
                    filterValue={
                        column.getFilterValue() as DateTimeRangeFilterValue
                    }
                    setFilterValue={column.setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        case "period": {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const labels = [...values.keys()];
            const options = labels.map((period) => ({
                label: period,
                value: period,
            }));

            return (
                <Select
                    options={options}
                    autoFocus
                    mode="multiple"
                    placeholder={t(
                        "ControlTestingActivityView_PeriodFilterPlaceholder"
                    )}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 200 }}
                    value={column.getFilterValue() ?? undefined}
                    onChange={(value) => column.setFilterValue(value)}
                />
            );
        }
        case "tags": {
            const rows = column.getFacetedRowModel();

            // Extract all unique categories and tags from the table data
            const categoryTagMap = new Map<string, Set<string>>();

            rows.flatRows.forEach((row) => {
                const rowTags = row.original.tags;
                Object.entries(rowTags).forEach(([categoryName, tagNames]) => {
                    if (!categoryTagMap.has(categoryName)) {
                        categoryTagMap.set(categoryName, new Set());
                    }
                    tagNames.forEach((tagName: string) => {
                        categoryTagMap.get(categoryName)!.add(tagName);
                    });
                });
            });

            // Convert to Cascader options format
            const options = Array.from(categoryTagMap.entries()).map(
                ([categoryName, tagNames]) => ({
                    label: categoryName,
                    value: categoryName,
                    children: Array.from(tagNames).map((tagName) => ({
                        label: tagName,
                        value: tagName,
                    })),
                })
            );

            return (
                <TagsCascader
                    options={options}
                    onChange={(_value, multiValue) =>
                        column.setFilterValue(multiValue)
                    }
                    placeholder={t(
                        "ControlTestingActivityView_TagsFilterPlaceholder"
                    )}
                />
            );
        }
        case "status": {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const labels = [...values.keys()];
            const options = labels.map((label) => ({ label, value: label }));
            return (
                <Select
                    options={options}
                    autoFocus
                    mode="multiple"
                    placeholder={t(
                        "ControlTestingActivityView_StatusFilterPlaceholder"
                    )}
                    dropdownMatchSelectWidth={false}
                    style={{ minWidth: 200 }}
                    value={column.getFilterValue() ?? undefined}
                    onChange={(value) => {
                        column.setFilterValue(value);
                    }}
                />
            );
        }
        case "title":
            throw new Error("Column should be marked as not filterable");
        default:
            return assertNever(columnId);
    }
}
